import { CheckmarkNewIcon } from '@app/components/icons/checkmark-new';
import { CloseNewIcon } from '@app/components/icons/close-new';
import { EditNewIcon } from '@app/components/icons/edit-new';
import { Spinner } from '@app/components/icons/spinner';
import { LazyEditor } from '@app/components/rich-text/lazy-editor';
import { LazyRenderer } from '@app/components/rich-text/lazy-renderer';
import { Recording } from '@app/routes/content/domain';
import { useAppDispatch, useAppSelector } from '@app/store';
import { useTranslation } from '@hopin-team/provider-translation';
import cx from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useRecordingAnalytics } from '../../../hooks';
import {
  selectRecording,
  selectRecordingDescriptionUpdateState,
} from '../../../selector';

export const Description = () => {
  const { t } = useTranslation();
  const { trackRecordingEvent } = useRecordingAnalytics();
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const { id: recordingId, description } = useAppSelector(
    selectRecording,
  ) as Recording;
  const descriptionRef = useRef<HTMLTextAreaElement>(null);
  const [descriptionValue, setDescriptionValue] = useState(
    description || '<p></p>',
  );
  const [showFullDescription, setShowFullDescription] = useState(false);
  const dispatch = useAppDispatch();
  const descriptionUpdateState = useAppSelector(
    selectRecordingDescriptionUpdateState,
  );
  const { status } = descriptionUpdateState;

  useEffect(() => {
    setDescriptionValue(description);
  }, [description]);

  useEffect(() => {
    if (descriptionRef.current && isEditingDescription) {
      descriptionRef.current.focus();
      descriptionRef.current.selectionStart =
        descriptionRef.current.value.length;
    }
  }, [isEditingDescription]);

  useEffect(() => {
    if (status !== 'processing') {
      setIsEditingDescription(false);
    }
  }, [status]);

  const handleDescriptionEdit = useCallback(() => {
    setIsEditingDescription(true);
  }, []);

  const handleDescriptionSave = useCallback(() => {
    if (description === descriptionValue) {
      setIsEditingDescription(false);
      return;
    }
    trackRecordingEvent('ch_recording_editor_description_updated');
    dispatch({
      type: '[recording] update description started',
      payload: {
        recordingId: recordingId,
        description: descriptionValue,
      },
    });
  }, [
    description,
    descriptionValue,
    dispatch,
    recordingId,
    trackRecordingEvent,
  ]);

  const handleDescriptionCancel = useCallback(() => {
    setIsEditingDescription(false);
  }, []);

  const handleDescriptionChange = useCallback(
    (_event: any, editor: any) => {
      setShowFullDescription(false);
      setDescriptionValue(editor.getData());
    },
    [setDescriptionValue, setShowFullDescription],
  );

  if (!description && !isEditingDescription) {
    return (
      <div className="px-2 pb-2">
        <button
          onClick={handleDescriptionEdit}
          className="flex items-center gap-2 px-4 py-2 border border-transparent hover:border-white/20 rounded-10 text-white text-[0.9375rem] font-medium leading-5 hover:bg-gray-800 transition-colors h-9"
        >
          <svg
            className="w-3.5 h-3.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          {t('content-recording.content-menu.add-description')}
        </button>
      </div>
    );
  }

  if (isEditingDescription) {
    return (
      <>
        <div className="flex items-center justify-between mb-2 px-2">
          <p className="text-white text-sm font-medium m-0">
            {t('content-recording.content-menu.description')}
          </p>
          <div className="flex items-center gap-2">
            {status === 'processing' ? (
              <Spinner className="w-4 h-4" theme="dark" />
            ) : (
              <>
                <button
                  onClick={handleDescriptionCancel}
                  className="text-white p-1 "
                  data-testid="description-cancel-button"
                >
                  <CloseNewIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={handleDescriptionSave}
                  className="text-white p-1 "
                  data-testid="description-save-button"
                >
                  <CheckmarkNewIcon className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
        </div>
        <LazyEditor
          name="description-textarea"
          toolbar="min"
          initialValue={descriptionValue}
          onChange={handleDescriptionChange}
          theme="dark"
          className="[&_.editor-toolbar_.ck-toolbar]:border-x-0 [&_.editor-toolbar]:border-y-spring-neutral-b4 [&_.editor-editable-container_.ck-content]:border-0 [&_.editor-editable-container_.ck-editor\_\_editable]:!border-none"
        />
      </>
    );
  }

  return (
    <div className="px-2 pb-2">
      <div className="text-white flex items-center justify-between mb-2">
        <p className=" text-sm font-medium m-0">
          {t('content-recording.content-menu.description')}
        </p>
        <button
          onClick={handleDescriptionEdit}
          className="p-1"
          data-testid="description-edit-button"
        >
          <EditNewIcon className="w-4 h-4" />
        </button>
      </div>
      <button
        className="text-left"
        onClick={() => setShowFullDescription(!showFullDescription)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setShowFullDescription(!showFullDescription);
          }
        }}
      >
        <LazyRenderer
          value={description}
          className={cx(
            'text-white text-sm font-normal leading-5 text-left overflow-hidden text-ellipsis m-0 bg-transparent border-none cursor-pointer',
            { 'line-clamp-4': showFullDescription === false },
          )}
        />
      </button>
    </div>
  );
};
